import { Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { ImageGridComponent, ImageGridItem } from '../image-grid/image-grid.component';
import { ImageGridThumbnailElementDirective } from '../image-grid/image-grid-thumbnail-element.directive';
import { ButtonComponent } from '../button/button.component';
import { PhotoSelectionDialogInput } from './photo-selection-dialog-input';

@Component({
  selector: 'app-photo-selection-dialog',
  templateUrl: './photo-selection-dialog.component.html',
  styleUrl: './photo-selection-dialog.component.scss',
  imports: [TranslateModule, ImageGridComponent, ImageGridThumbnailElementDirective, ButtonComponent, MatIconModule],
})
export class PhotoSelectionDialogComponent {
  #dialogRef = inject(MatDialogRef<PhotoSelectionDialogComponent>);
  #dialogData = inject<PhotoSelectionDialogInput>(MAT_DIALOG_DATA);
  #initialSelectedIndexes = this.#dialogData.photos.reduce(
    (set, photo, index) => (photo.selected ? set.add(index) : set),
    new Set<number>(),
  );
  public readonly selectedPhotoIndexes = signal<Set<number>>(new Set(this.#initialSelectedIndexes));

  public readonly images = computed<ImageGridItem[]>(() =>
    this.#dialogData.photos.map((photo, index) => ({
      src: photo.imageUrl,
      onClick: () => this.togglePhotoSelection(index),
    })),
  );

  public isSelected(photoIndex: number): boolean {
    return this.selectedPhotoIndexes().has(photoIndex);
  }

  public togglePhotoSelection(index: number): void {
    this.selectedPhotoIndexes.update(currentSelection => {
      const newSelection = new Set(currentSelection);
      if (newSelection.has(index)) {
        newSelection.delete(index);
      } else {
        newSelection.add(index);
      }
      return newSelection;
    });
  }

  public onConfirm(): void {
    const currentSelection = this.selectedPhotoIndexes();
    const photos = this.#dialogData.photos;

    const addedPhotos = photos.filter((_, index) => currentSelection.has(index) && !this.#initialSelectedIndexes.has(index));
    const removedPhotos = photos.filter((_, index) => !currentSelection.has(index) && this.#initialSelectedIndexes.has(index));

    this.#dialogRef.close({ addedPhotos, removedPhotos });
  }

  public onCancel(): void {
    this.#dialogRef.close();
  }
}
